/**
 * Deterministic randomness for A LIFE.
 *
 * Two complementary strategies are provided:
 *
 * 1. `Rng` — a sequential stream. Its entire state is `{ seed, cursor }`, so it
 *    serialises into a save file and resumes exactly where it left off.
 * 2. `keyed()` — a stream derived from a stable label (e.g. `market:2031`).
 *    Keyed streams are independent of call order, which means a market return
 *    for a given year is the same whether or not the player triggered an event
 *    earlier in that year. That property makes balance regressions debuggable.
 *
 * Nothing in this module touches global state or `Math.random`.
 */

/** xmur3 string hash — produces a well-distributed 32-bit seed from a string. */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** mulberry32 — small, fast, statistically adequate for gameplay. */
function mulberry32(a: number): () => number {
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface RngState {
  seed: string;
  cursor: number;
}

export interface WeightedItem {
  weight: number;
}

export class Rng {
  readonly seed: string;
  private cursor: number;

  constructor(seed: string, cursor = 0) {
    this.seed = seed;
    this.cursor = cursor;
  }

  static fromState(state: RngState): Rng {
    return new Rng(state.seed, state.cursor);
  }

  toState(): RngState {
    return { seed: this.seed, cursor: this.cursor };
  }

  /** A fresh, order-independent stream bound to `label`. */
  keyed(label: string): Rng {
    return new Rng(`${this.seed}::${label}`, 0);
  }

  /** Next float in [0, 1). Advances the cursor. */
  next(): number {
    const h = xmur3(`${this.seed}#${this.cursor}`);
    this.cursor += 1;
    return mulberry32(h())();
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    if (max < min) return min;
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** True with probability `p` (clamped to [0, 1]). */
  chance(p: number): boolean {
    if (p <= 0) return false;
    if (p >= 1) return true;
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T | undefined {
    if (items.length === 0) return undefined;
    return items[this.int(0, items.length - 1)];
  }

  /** Pick `count` distinct items (or fewer if the pool is smaller). */
  sample<T>(items: readonly T[], count: number): T[] {
    const pool = [...items];
    const out: T[] = [];
    while (out.length < count && pool.length > 0) {
      const idx = this.int(0, pool.length - 1);
      out.push(pool[idx] as T);
      pool.splice(idx, 1);
    }
    return out;
  }

  shuffle<T>(items: readonly T[]): T[] {
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const a = arr[i] as T;
      arr[i] = arr[j] as T;
      arr[j] = a;
    }
    return arr;
  }

  /** Weighted selection. Items with weight <= 0 are ignored. */
  weighted<T extends WeightedItem>(items: readonly T[]): T | undefined {
    let total = 0;
    for (const item of items) if (item.weight > 0) total += item.weight;
    if (total <= 0) return undefined;
    let roll = this.next() * total;
    for (const item of items) {
      if (item.weight <= 0) continue;
      roll -= item.weight;
      if (roll <= 0) return item;
    }
    return items.find((i) => i.weight > 0);
  }

  /** Weighted selection over parallel arrays. */
  weightedIndex(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) if (w > 0) total += w;
    if (total <= 0) return -1;
    let roll = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      const w = weights[i] as number;
      if (w <= 0) continue;
      roll -= w;
      if (roll <= 0) return i;
    }
    return weights.findIndex((w) => w > 0);
  }

  /** Normal deviate via Box–Muller, clamped to ±4 sigma to avoid absurd tails. */
  normal(mean = 0, sd = 1): number {
    const u1 = Math.max(this.next(), 1e-12);
    const u2 = this.next();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return mean + sd * Math.max(-4, Math.min(4, z));
  }

  /** Log-normal-ish multiplier useful for investment returns. */
  logNormal(mean: number, sd: number): number {
    return Math.exp(this.normal(Math.log(1 + mean) - (sd * sd) / 2, sd)) - 1;
  }
}

/** Create a random-looking but valid seed string. */
export function makeSeed(entropy: () => number = Math.random): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 12; i++) {
    out += alphabet[Math.floor(entropy() * alphabet.length)] ?? 'A';
  }
  return out;
}
